import React from "react";
import { Grid } from "@mui/material";
import { ConfigSection } from "../ConfigSection";
import { StyledTextField } from "../StyledTextField";
import { validateBoostValue } from "../../utils/validation";
import { BoostConfigSectionProps } from "../../types";

export const BoostConfigSection: React.FC<BoostConfigSectionProps> = ({
  config,
  updateBoostConfig,
}) => (
  <ConfigSection
    title="Query Boost Configuration"
    tooltip="These settings control how much importance is given to different types of search matches. Think of it like giving bonus points to certain kinds of results. Higher numbers mean those results will appear first in your search results. For example, if you boost 'exact matches' highly, results that match your search term exactly will show up before partial matches."
  >
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Wildcard Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.wildcard ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              wildcard: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Matches patterns like appl* or *fruit*. Useful for partial searches. Range: 0–100,000. Default: 500. Impact: Moderate performance cost, high relevance for partial matches."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Fuzzy Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.fuzzy ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              fuzzy: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Handles typos and misspellings (e.g., “aple” → “apple”). Range: 0–100,000. Default: 500. Impact: High performance cost, medium relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Exact Term Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.termExact ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              termExact: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Strongest boost for perfect keyword matches (e.g., “iPhone 15”). Range: 0–100,000. Default: 10,000. Impact: Low performance cost, highest relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="First Word Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.termFirstWord ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              termFirstWord: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Extra weight when the first word of a search matches exactly (e.g., “apple juice”). Range: 0–100,000. Default: 3,000. Impact: Low performance cost, high relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="General Term Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.termGeneral ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              termGeneral: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Boost for standard term matches in analyzed text. Range: 0–100,000. Default: 2,000. Impact: Low performance cost, medium-high relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Match Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.match ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              match: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Boost when all search terms appear together in a field. Range: 0–100,000. Default: 500. Impact: Low performance cost, medium relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Prefix Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.prefix ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              prefix: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Matches words starting with the search term (great for autocomplete). Range: 0–100,000. Default: 500. Impact: Medium performance cost, high relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Multi-Match Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.multiMatch ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              multiMatch: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Searches across multiple fields simultaneously (title, description, etc.). Range: 0–100,000. Default: 50. Impact: Medium performance cost, medium relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Match Phrase Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.matchPhrase ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              matchPhrase: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Finds exact phrases (e.g., “red apple”), not just separate words. Range: 0–100,000. Default: 1,000. Impact: Medium performance cost, very high relevance."
        />
      </Grid>
      <Grid item xs={6}>
        <StyledTextField
          fullWidth
          label="Match Phrase Prefix Boost"
          type="number"
          inputProps={{ step: 1, min: 0, max: 100000 }}
          value={config.boostConfig?.matchPhrasePrefix ?? ""}
          validate={validateBoostValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateBoostConfig({
              matchPhrasePrefix: value === "" ? undefined : parseFloat(value),
            });
          }}
          helperText="Matches phrases where the last word is partial (e.g., “red app” → “red apple”). Range: 0–100,000. Default: 1,000. Impact: Medium performance cost, very high relevance."
        />
      </Grid>
    </Grid>
  </ConfigSection>
);
